import { useEffect, useState, useRef, useMemo } from 'react';
import { tokens } from "../../theme";
import Header from "../../components/Header";
import {
    useTheme, Box, Container, Grid, Card, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, Typography, Paper, Autocomplete,
    ToggleButton, ToggleButtonGroup, Popper, CircularProgress, MenuItem
} from '@mui/material';

import { AreaSeries, createChart, ColorType, LineSeries, CrosshairMode, PriceScaleMode } from 'lightweight-charts';

import { __fetching_data__ } from '../../components/api';
import { date } from 'yup';
import { showLoader, hideLoader } from '../../components/Preloading2';

// import Payloads1 from '../../Mock1.js'
// import Payloads2 from '../../Mock2.js'
// set PORT=3001 && npm start

function createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting, color, serieInstanceRefs, key, seriesType, seriesParam, idx) {
    const data = prepareData(arr, selectedYear, seriesType);
    // const series chartInstanceRefs.current[idx].addLineSeries(seriesSetting.mainSeries1);
    const series = chartInstanceRefs.current[idx].addLineSeries(seriesSetting);
    series.setData(data);
    chartInstanceRefs.current[idx].timeScale().fitContent();
    series.applyOptions({ color, lastValueVisible: false, visible: false });
    serieInstanceRefs.current[seriesParam][key] = series;
}

const compareDatetime = (a, b, field = 'DateTime') => {
    const datetimeA = new Date(a[field]);
    const datetimeB = new Date(b[field]);
    return datetimeA - datetimeB;
}

const filterByRange = (data, range) => {
    if (range === 'ALL') return data;

    const currentDate = new Date();
    let fromDate;

    switch (range) {
        case '3M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 3);
            break;
        case '6M':
            fromDate = new Date(currentDate);
            fromDate.setMonth(currentDate.getMonth() - 6);
            break;
        case '1Y':
            fromDate = new Date(currentDate);
            fromDate.setFullYear(currentDate.getFullYear() - 1);
            break;
        case 'YTD':
            fromDate = new Date(currentDate.getFullYear(), 0, 1); // Jan 1st of current year
            break;
        default:
            console.warn(`Unsupported range: ${range}`);
            return data;
    }

    const filteredData = data
        .filter(item => {
            const itemDate = new Date(item.Date || item.date);
            return itemDate >= fromDate && itemDate <= currentDate;
        }
        )

    return filteredData;
};

const generateData = (data, date_columm = 'DateTime', field) => {
    const timeZoneOffset = 7 * 60 * 60 * 1000;
    const cdata = data.map(d => {
        let time = timeToTz(d[date_columm], 'Asia/Bangkok')

        const dd = new Date(d[date_columm])
        const year = dd.getFullYear();
        const month = String(dd.getMonth() + 1).padStart(2, '0'); // Add 1 because months are zero-indexed
        const day = String(dd.getDate()).padStart(2, '0');

        let time2 = `${year}-${month}-${day}`;

        return { time: time2, value: parseFloat(d[field]) || 0 }
    });
    return cdata;
}
function timeToTz(originalTime, timeZone) {
    const zonedDate = new Date(new Date(originalTime).toLocaleString('en-US', { timeZone }));
    return zonedDate.getTime() / 1000;
}

const generateUniqueRandomColors = (count) => {
    const colors = [
        "#FFC300", // Bright Yellow
        "#8BC34A", // Light Green
        "#A8E6CF", // Mint Green
        "#C9CBCF", // Light Grey
        "#4BC0C0", // Light Teal
        "#009688", // Teal
        "#FF6384", // Light Red
        "#36A2EB", // Light Blue
        "#FFCE56", // Soft Yellow
        "#9966FF", // Light Purple
        "#FF9F40", // Light Orange
        "#FFD3B6", // Peach
        "#FFAAA5", // Soft Red
        "#D1C4E9", // Lavender
        "#B3E5FC", // Baby Blue
        "#DCEDC8", // Pale Green
        "#FFF9C4", // Pale Yellow
        "#FFECB3"  // Light Amber
    ];

    const usedColors = new Set();

    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    // Function to shuffle array
    function shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    const shuffledColors = shuffleArray(colors);

    while (shuffledColors.length < count) {
        //const color = getRandomColor();
        if (!usedColors.has(colors)) {
            usedColors.add(colors);
            colors.push(colors);
        }
    }

    return colors;
};

const base_api_url =
    process.env.NODE_ENV === "production"
        ? window.location.protocol === "https:"
            ? process.env.REACT_APP_API_HTTPS_URL
            : process.env.REACT_APP_API_HTTP_URL
        : "/application";

const prepareData = (arr, year, valueKey) => {
    const values = filterByRange(arr, year).sort((a, b) => compareDatetime(a, b, 'date'));
    const gen = generateData(values, 'date', valueKey);
    // Clean duplicate dates
    return Object.values(gen.reduce((acc, item) => {
        acc[item.time] = item;
        return acc;
    }, {}));
};

const BidAskComponent = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [options, setOpeions] = useState([]);
    const [selectedYear, setSelectedYear] = useState('3M');

    const [paramsQuery, setParamsQuery] = useState({
        symbol: "",
        queryType: 3,
    });

    const [selectedSymbol, setSelectedSymbol] = useState(null);

    const [groupByPetroleumName, setGroupByPetroleumName] = useState({});
    const [symbolPrice, setSymbolPrice] = useState({});

    const isEmpty = (obj) => {
        return Object.keys(obj).length === 0;
    }

    const [isSearch, setIsSearch] = useState(true);
    const [chartsReady, setChartsReady] = useState(false);
    const [groupedDataState, setGroupedDataState] = useState({});

    const get_data = async (symbol, queryType) => {
        if (!chartInstanceRefs.current[0]) {
            return;
        }
        try {
            // Update paramsQuery only if needed
            if (symbol !== undefined || queryType !== undefined) {
                setParamsQuery(prev => ({
                    ...prev,
                    symbol: symbol ?? prev.symbol,
                    queryType: queryType ?? prev.queryType
                }));
            }
            // const response = Payloads1;
            const response = await __fetching_data__({ url: `${base_api_url}/petroleum_report?${new URLSearchParams(paramsQuery)}`, method: "GET" });
            if (!response.status) return;

            // Prepare groupedData
            let groupedData = groupByPetroleumName;
            let sortedData = symbolPrice;
            if (isEmpty(groupedData) && response.data2) {
                // Group by name and filter within past month
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 2); // Calculate one month ago

                groupedData = response.data2.reduce((group, product) => {
                    const name = product.name.trim();
                    group[name] = group[name] || [];
                    group[name].push(product);
                    return group;
                }, {});


                groupedData = Object.fromEntries(
                    Object.entries(groupedData).filter(([_, arr]) =>
                        arr.some(product => new Date(product.date) >= oneMonthAgo)
                    )
                );
                setGroupByPetroleumName(groupedData);
                uniqueRandomColors.current = generateUniqueRandomColors(Object.keys(groupedData).length);
            }

            // Prepare sortedData if needed
            if (response.data) {
                sortedData = [...response.data].sort((a, b) => compareDatetime(a, b, 'Date'));
                setSymbolPrice(sortedData);
            }

            // Extract year and option keys once
            // const year = extractNumberFromString(selectedYear);
            const optionKeys = Object.keys(groupedData);

            setGroupedDataState(groupedData);
            setOpeions(optionKeys);

            // Main chart for symbol price
            // const mainData = filterByYears(sortedData, year);
            const mainData = filterByRange(sortedData, selectedYear);
            // const mainSeries = chartInstanceRefs.current[0].addSeries(LineSeries, {
            //     ...seriesSetting.mainSeries1
            // });
            const mainSeries = chartInstanceRefs.current[0].addLineSeries(seriesSetting.mainSeries1);
            mainSeries.setData(generateData(mainData, 'Date', 'price'));
            serieInstanceRefs.current.close['close'] = mainSeries;

            // Utility for cleaning and generating chart data


            // Loop for each oil type and create series for all metrics
            optionKeys.forEach((key, idx) => {
                const color = uniqueRandomColors.current[idx];
                const arr = groupedData[key];

                createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries2, color, serieInstanceRefs, key, 'ex_refin', 'ex_refin', 1); // ex_refin
                createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries3, color, serieInstanceRefs, key, 'marketing', 'mkt_margin', 2); // mkt_margin
                createSeries(arr, selectedYear, chartInstanceRefs, seriesSetting.mainSeries4, color, serieInstanceRefs, key, 'oil_fund', 'oil_fund', 3); // oil_fund
            });

            // Synchronize chart logic
            try {
                const synchronizeCharts = () => {
                    const keys_series = Object.keys(serieInstanceRefs.current);
                    const filteredCharts = chartInstanceRefs.current.filter((_, idx) => [1, 2, 3].includes(idx));
                    filteredCharts.forEach((currentChart, currentIdx) => {
                        currentChart.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                            filteredCharts.forEach((otherChart, otherIdx) => {
                                if (currentIdx !== otherIdx) {
                                    otherChart.timeScale().setVisibleLogicalRange(timeRange);
                                }
                            });
                        });
                    });
                    filteredCharts.forEach((currentChart, currentIdx) => {
                        currentChart.subscribeCrosshairMove(param => {
                            const firstKey = Object.keys(serieInstanceRefs.current[keys_series[currentIdx + 1]])[0];
                            const dataPoint = getCrosshairDataPoint(serieInstanceRefs.current[keys_series[currentIdx + 1]][firstKey], param);
                            if (dataPoint !== undefined && dataPoint !== null) {
                                filteredCharts.forEach((otherChart, otherIdx) => {
                                    if (currentIdx !== otherIdx) {
                                        Object.keys(serieInstanceRefs.current[keys_series[otherIdx + 1]]).forEach(key_other_serie => {
                                            if (Object.hasOwnProperty.call(serieInstanceRefs.current[keys_series[otherIdx + 1]], key_other_serie)) {
                                                syncCrosshair(
                                                    otherChart,
                                                    serieInstanceRefs.current[keys_series[otherIdx + 1]][key_other_serie],
                                                    dataPoint
                                                );
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    });
                };
                synchronizeCharts();
            } catch (err) {
                console.log(err);
            }
            setIsSearch(false);
            return response;
        } catch (error) {
            console.error("Error fetching petroleum report:", error);
        }
    };

    const [chartContainers, setChartContainers] = useState([]);
    const chartInstanceRefs = useRef([]);

    const charts_option = [
        {
            width: 500,
            height: 30,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true,
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                background: {
                    type: 'solid',
                    color: '#575b69',
                },
                textColor: '#ffffff', // เพิ่มการตั้งค่าสีตัวหนังสือเป็นสีขาว
            },
            grid: {
                vertLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark,
                horzLines: { color: "#333" } //|| color: theme.palette.neutral?.dark,
            },
            rightPriceScale: {

                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: true,
                drawTicks: false,
            },
            lastValueVisible: false,

        }, {
            width: 500,
            height: 30,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                background: {
                    type: 'solid',
                    color: '#575b69',
                },
                textColor: '#ffffff', // เพิ่มการตั้งค่าสีตัวหนังสือเป็นสีขาว
            },
            grid: {
                vertLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark
                horzLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: false,
                drawTicks: true,
            },
            lastValueVisible: false
        }, {
            width: 500,
            height: 30,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                background: {
                    type: 'solid',
                    color: '#575b69',
                },
                textColor: '#ffffff', // เพิ่มการตั้งค่าสีตัวหนังสือเป็นสีขาว
            },
            grid: {
                vertLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark
                horzLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: false,
                drawTicks: true,
            },
            lastValueVisible: false
        }, {
            width: 500,
            height: 300,
            crosshair: {
                mode: 1
            },
            timeScale: {
                visible: true
            },
            localization: {
                locale: "en-US"
            },
            layout: {
                background: {
                    type: 'solid',
                    color: '#575b69',
                },
                textColor: '#ffffff', // เพิ่มการตั้งค่าสีตัวหนังสือเป็นสีขาว
            },

            grid: {
                vertLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark
                horzLines: { color: "#333" }, //|| color: theme.palette.neutral?.dark
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.2,
                },
                borderVisible: false,
                drawTicks: true,
            },
            lastValueVisible: false
        }
    ]

    const memoizedChartContainers = useMemo(() => chartContainers, [chartContainers]);
    const uniqueRandomColors = useRef([]);
    const [seriesSetting, setSeriesSetting] = useState({
        'mainSeries1': {
            color: "#ff9966",
            lineWidth: 3,
            priceLineVisible: false,
            lastValueVisible: false
        },
        'mainSeries2': {
            color: "#33ffcc",
            lineWidth: 3,
            priceLineVisible: false,
        },
        'mainSeries3': {
            color: "#ff9966",
            lineWidth: 3,
            priceLineVisible: false,
        },
        'mainSeries4': {
            color: "#66ffcc",
            lineWidth: 3,
            priceLineVisible: false,
        },
    });

    const [selectedOptions, setSelectedOptions] = useState([]);
    const chart_name = ['CLOSE', 'EX-REFIN', 'Marketing Margin', 'Oil Fund', 'CLOSE', 'EX-REFIN', 'Marketing Margin', 'Oil Fund'];
    useEffect(() => {
        const content = document.getElementsByClassName('content')[0];
        if (!content) return;
        // const resizeHandler = () => {
        //     chartInstanceRefs.current.forEach((chart, index) => {
        //         if (chart) {
        //             chart.resize(chartContainers[index].clientWidth, chartContainers[index].clientHeight);
        //         }
        //     })
        // };
        // const resizeObserver = new ResizeObserver(resizeHandler);
        // resizeObserver.observe(content);
        const chartAnnotation = (chart, text) => {
            chart.applyOptions({
                watermark: {
                    visible: true,
                    fontSize: 30,
                    horzAlign: 'center',
                    vertAlign: 'top',
                    color: 'rgba(255, 255, 255, 0.6)',
                    text: text,
                },
                rightPriceScale: { visible: true },
                leftPriceScale: {
                    visible: true,
                    mode: PriceScaleMode.Normal,
                    autoScale: false,
                    invertScale: false,
                    alignLabels: false,
                    minValue: -1,
                    maxValue: 1,
                },
            });
        }
        const createChartInstances = () => {
            chartContainers.forEach((container, index) => {
                const chart = createChart(container, charts_option[index ? index : 0]);
                chartAnnotation(chart, chart_name[index])
                // console.log('Chart title:', currentChartTitle); // logs the title text
                chartInstanceRefs.current[index] = chart;
                chart.resize(chartContainers[index].clientWidth, resChartRef.current.clientHeight / 2);

            });
            setChartsReady(true); // <-- Set chartsReady after all charts are created
            // window.addEventListener('resize', resizeHandler);
            return () => {
                // window.removeEventListener('resize', resizeHandler);
                chartInstanceRefs.current.forEach((chart) => {
                    if (chart) {
                        chart.remove();
                    }
                });
            };
        };
        createChartInstances();
        // console.log('create chart called');
        return () => {
            get_data();
            // resizeObserver.disconnect();
        };
    }, [memoizedChartContainers]);

    const handleAddChartContainer = (element) => {
        if (!chartContainers.includes(element) && element) {
            setChartContainers((prevContainers) => [...prevContainers, element]);
        }
    };
    const handleYearChange = (event, newValue) => {
        setSelectedYear(newValue);

    };

    const handleChange = (e, selectedValue) => {
        setParamsQuery((prevData) => ({
            ...prevData,
            'symbol': selectedValue,
            queryType: 4,
        }));
    }

    const [crosshairDataPoint, setCrosshairDataPoint] = useState(null);
    function getCrosshairDataPoint(series, param) {
        if (!param.time) {
            return null;
        }
        const dataPoint = param.seriesData.get(series);
        return dataPoint || null;
    }

    function syncCrosshair(chart, series, dataPoint) {
        // console.log('syncCrosshair called with:', { chart, series, dataPoint });
        if (dataPoint) {
            // console.log('Setting crosshair position:', { value: dataPoint.value, time: dataPoint.time });
            chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
            return;
        }
        // console.log('Clearing crosshair position');
        chart.clearCrosshairPosition();
    }

    const serieInstanceRefs = useRef({ close: {}, ex_refin: {}, mkt_margin: {}, oil_fund: {} });
    useEffect(() => {
    }, [serieInstanceRefs, selectedOptions]);

    const clearAllSeries = () => {
        const chartRef = chartInstanceRefs.current;
        const serieRef = serieInstanceRefs.current;

        ['close', 'ex_refin', 'mkt_margin', 'oil_fund'].forEach((type, idx) => {
            const seriesMap = serieRef[type];
            Object.values(seriesMap).forEach(series => {
                try {
                    chartRef[idx]?.removeSeries(series);
                } catch (e) {
                    console.warn(`Failed to remove series for ${type}`, e);
                }
            });
            serieRef[type] = {}; // reset reference
        });
    };


    useEffect(() => {
        if (chartsReady) {
            clearAllSeries();  // remove old series
            get_data().then(() => {
                handleSelectionChange(null, selectedOptions); // run AFTER series is set
            });
        }
    }, [selectedYear, chartsReady]);

    function getCrosshairDataPoint(series, param) {
        if (!param.time) {
            return null;
        }
        const dataPoint = param.seriesData.get(series);
        return dataPoint || null;
    }

    // Select Oil Type
    const handleSelectionChange = (event, newSelectedOptions) => {

        options.forEach((key, value) => {
            serieInstanceRefs.current.ex_refin[key].applyOptions({
                visible: false
            });
            serieInstanceRefs.current.mkt_margin[key].applyOptions({
                visible: false
            });
            serieInstanceRefs.current.oil_fund[key].applyOptions({
                visible: false
            });
        });
        setSelectedOptions(newSelectedOptions);

        newSelectedOptions.forEach((key, value) => {
            serieInstanceRefs.current.ex_refin[key].applyOptions({
                visible: true
            });
            serieInstanceRefs.current.mkt_margin[key].applyOptions({
                visible: true
            });
            serieInstanceRefs.current.oil_fund[key].applyOptions({
                visible: true
            });
        });
    };

    const [symbols, setSymbols] = useState([]);
    useEffect(() => {
        const fetchData = async () => {
            try {
                showLoader();
                const response = await __fetching_data__({ url: `${base_api_url}/petroleum_symbol_list`, method: "GET" });
                if (response.status) {
                    response.data.forEach((val, index) => {
                        setSymbols((prevSymbols) => [...prevSymbols, val.SymbolName]);
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                hideLoader();
            }
        };
        fetchData();
    }, []);

    const resChartRef = useRef(null);
    const resToolsRef = useRef(null);
    const [gridHeight, setGridHeight] = useState(0);
    useEffect(() => {
        // Ensure the reference is available
        if (!resChartRef.current) return;

        // Get the height of the Grid component
        const height = resChartRef.current.getBoundingClientRect().height;
        setGridHeight(height);


        const remainingHeight = `calc(100vh - ${90}px)`;
        // console.log(remainingHeight)
        resChartRef.current.style.height = remainingHeight;

    }, []);

    return (
        <Box m="20px">
            <Box display="block">
                <Grid container spacing={2}
                    sx={{
                        marginBottom: "1rem"
                    }}
                    ref={resToolsRef}
                >
                    <Grid item xs={4} md={2}>
                        <Autocomplete
                            id="symbol"
                            options={symbols}
                            autoComplete
                            autoHighlight
                            fullWidth
                            inputValue={paramsQuery.symbol}
                            onInputChange={handleChange}
                            // onChange={(event, newValue) => {
                            //     setSelectedSymbol(newValue);
                            // }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Symbol"
                                    variant="outlined"
                                    required
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {/* {loading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null} */}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option, { index }) => (
                                <MenuItem {...props} key={`${option}-${index}`}>
                                    <Typography variant="inherit">{option}</Typography>
                                </MenuItem>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            multiple
                            options={options}
                            value={selectedOptions}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Select oil type"
                                    placeholder="select oil type"
                                />
                            )}
                            size='small'
                            onChange={handleSelectionChange}
                        />
                    </Grid>

                    <Grid item xs={4} md={2}>
                        <ToggleButtonGroup
                            value={selectedYear}
                            exclusive
                            onChange={handleYearChange}
                            aria-label="Basic radio toggle button group"
                            size="small"
                            fullWidth
                        >
                            <ToggleButton value="3M" aria-label="1Y" sx={{ color: 'white' }}>
                                3M
                            </ToggleButton>
                            <ToggleButton value="6M" aria-label="3Y" sx={{ color: 'white' }}>
                                6M
                            </ToggleButton>
                            <ToggleButton value="1Y" aria-label="5Y" sx={{ color: 'white' }}>
                                1Y
                            </ToggleButton>
                            <ToggleButton value="YTD" aria-label="5Y" sx={{ color: 'white' }}>
                                YTD
                            </ToggleButton>
                            <ToggleButton value="ALL" aria-label="ALL" sx={{ color: 'white' }}>
                                MAX
                            </ToggleButton>
                        </ToggleButtonGroup>

                    </Grid>
                    <Grid item xs={4} md={2}>
                        {/* 
                        <Button variant="contained"
                            sx={{
                                backgroundColor: colors.blueAccent[500]
                            }}
                            type="button"
                            onClick={handleSearchSymbol}
                        >
                            Search
                        </Button>*/}
                    </Grid>
                </Grid>

                <Grid
                    ref={resChartRef}
                    container
                    spacing={0}
                    sx={{
                        color: '#ffffff', // เพิ่มสีตัวอักษรเป็นสีขาว

                        // marginBottom: "1rem",
                        // backgroundColor: "red",
                        height: 'calc(100vh - 120px)',
                        minHeight: '500px',
                    }}
                >
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-close" style={{ borderRadius: '10px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-ex-refin" style={{ borderRadius: '10px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-marketing-margin" style={{ borderRadius: '10px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ padding: '2px', height: '50%' }}>
                        <div id="chart-oil-fund" style={{ borderRadius: '10px', overflow: 'hidden', height: '100%' }} ref={(element) => handleAddChartContainer(element)}></div>
                    </Grid>
                </Grid>

            </Box>
        </Box>
    )

}
export default BidAskComponent;